/**
 * Shared transport for quote providers.
 *
 * Routing rule: if a proxy is configured, every provider goes through it. Aggregator
 * API keys must never reach the browser bundle - anything in a REACT_APP_* variable is
 * readable by anyone who opens devtools, and a leaked key gets rate-limited or billed
 * against us. The proxy holds the keys and this layer never sees them.
 *
 * Providers that need no key (OpenOcean) may still be called directly, which is what
 * makes local development possible with no backend running.
 */

export const QUOTE_PROXY_URL = process.env.REACT_APP_QUOTE_PROXY_URL?.replace(/\/$/, '')

export const hasQuoteProxy = Boolean(QUOTE_PROXY_URL)

export class QuoteHttpError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message)
    this.name = 'QuoteHttpError'
  }
}

export function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.append(key, String(value))
  })
  return search.toString()
}

/**
 * Endpoint for a provider: the proxy when configured, otherwise the provider's own
 * public API. `proxyPath` is the provider id, so the proxy can route by it.
 */
export function endpointFor(providerId: string, directBase: string, path: string): string {
  return QUOTE_PROXY_URL ? `${QUOTE_PROXY_URL}/${providerId}${path}` : `${directBase}${path}`
}

export async function getJson<T>(url: string, signal: AbortSignal, headers?: Record<string, string>): Promise<T> {
  const response = await fetch(url, { signal, headers, method: 'GET' })

  if (!response.ok) {
    // Aggregators put the useful part of a failure in the body, not the status text -
    // "insufficient liquidity" and "rate limited" are both HTTP 400 otherwise.
    let detail = ''
    try {
      const text = await response.text()
      detail = text.slice(0, 300)
    } catch {
      /* body already consumed or unreadable */
    }

    if (response.status === 429) {
      throw new QuoteHttpError(429, 'Rate limited by the routing provider')
    }
    throw new QuoteHttpError(response.status, detail || `Request failed with status ${response.status}`)
  }

  return (await response.json()) as T
}
